import { Motion } from '@capacitor/motion'
import { Capacitor, PluginListenerHandle } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { ref } from 'vue'
import { usePostHog } from './usePostHog'
import { AnalyticsEvent } from '@/constants/analyticsEvents'

const SHAKE_ENABLED_KEY = 'shakeToReportBugEnabled'
const SHAKE_THRESHOLD = 12
const SHAKE_WINDOW_MS = 900
const SHAKE_COOLDOWN_MS = 3000
const MOTION_PERMISSION_EVENTS = ['pointerdown', 'touchend', 'click'] as const
const NATIVE_SHAKE_EVENT = 'balance:native-shake'
const SHAKE_DEBUG_ENABLED = import.meta.env.DEV

const enabled = ref(true)
const initialized = ref(false)
let motionListener: PluginListenerHandle | null = null
let nativeShakeListenerAttached = false
let gesturePermissionArmed = false
let lastShakeAt = 0
let lastMotionLogAt = 0
let recentShakeHits: number[] = []

function logShakeDebug(message: string, details?: Record<string, unknown>) {
  if (!SHAKE_DEBUG_ENABLED) return
  console.debug(`[shake] ${message}`, details || '')
}

function emitOpenBugReport(source: 'shake' | 'manual') {
  window.dispatchEvent(
    new CustomEvent('balance:open-bug-report', {
      detail: { source },
    }),
  )
}

function openBugReportFromShake(trigger: 'native' | 'motion') {
  const now = Date.now()
  if (now - lastShakeAt <= SHAKE_COOLDOWN_MS) {
    logShakeDebug('shake ignored during cooldown', { trigger })
    return
  }

  lastShakeAt = now
  recentShakeHits = []
  logShakeDebug('shake detected; opening report prompt', { trigger })
  Haptics.impact({ style: ImpactStyle.Medium }).catch(() => undefined)
  emitOpenBugReport('shake')
}

function getMotionPermissionRequester():
  | (() => Promise<'granted' | 'denied'>)
  | undefined {
  const deviceMotionEvent = window.DeviceMotionEvent as
    | (typeof DeviceMotionEvent & {
        requestPermission?: () => Promise<'granted' | 'denied'>
      })
    | undefined

  return deviceMotionEvent?.requestPermission
}

async function requestMotionPermissionFromGesture(): Promise<boolean> {
  const requestPermission = getMotionPermissionRequester()

  if (!requestPermission) {
    logShakeDebug('no DeviceMotionEvent.requestPermission API; continuing')
    return true
  }

  try {
    const result = await requestPermission()
    logShakeDebug('motion permission result', { result })
    return result === 'granted'
  } catch (error) {
    console.warn('Motion permission request failed:', error)
    return false
  }
}

function armMotionPermissionAfterNextGesture() {
  if (
    gesturePermissionArmed ||
    motionListener ||
    !getMotionPermissionRequester()
  ) {
    logShakeDebug('motion permission gesture arm skipped', {
      gesturePermissionArmed,
      hasListener: Boolean(motionListener),
      hasPermissionRequester: Boolean(getMotionPermissionRequester()),
    })
    return
  }

  gesturePermissionArmed = true
  logShakeDebug('arming motion permission on next user gesture')

  const handleGesture = () => {
    gesturePermissionArmed = false
    logShakeDebug('user gesture captured; requesting motion permission')
    MOTION_PERMISSION_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleGesture, {
        capture: true,
      })
    })
    startListening({ requestPermission: true }).catch((error) => {
      console.warn('Shake-to-report listener failed to start:', error)
    })
  }

  MOTION_PERMISSION_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleGesture, {
      capture: true,
      once: true,
    })
  })
}

async function ensureMotionPermission(requestPermission: boolean) {
  if (!getMotionPermissionRequester()) {
    logShakeDebug('permission requester unavailable', {
      platform: Capacitor.getPlatform(),
      userAgent: window.navigator?.userAgent,
    })
    return true
  }

  if (!requestPermission) {
    armMotionPermissionAfterNextGesture()
    return false
  }

  return requestMotionPermissionFromGesture()
}

async function stopListening() {
  if (motionListener) {
    await motionListener.remove()
    motionListener = null
  }

  if (nativeShakeListenerAttached) {
    window.removeEventListener(NATIVE_SHAKE_EVENT, handleNativeShake)
    nativeShakeListenerAttached = false
  }

  recentShakeHits = []
}

function handleNativeShake() {
  logShakeDebug('native iOS shake event received')
  openBugReportFromShake('native')
}

function startNativeShakeListening() {
  if (nativeShakeListenerAttached) return

  nativeShakeListenerAttached = true
  window.addEventListener(NATIVE_SHAKE_EVENT, handleNativeShake)
  logShakeDebug('attached native iOS shake listener')
}

async function startListening(options: { requestPermission: boolean }) {
  if (!Capacitor.isNativePlatform()) return

  if (Capacitor.getPlatform() === 'ios') {
    startNativeShakeListening()
    return
  }

  if (motionListener) return

  logShakeDebug('starting listener', {
    platform: Capacitor.getPlatform(),
    requestPermission: options.requestPermission,
    hasPermissionRequester: Boolean(getMotionPermissionRequester()),
    threshold: SHAKE_THRESHOLD,
  })

  const hasPermission = await ensureMotionPermission(options.requestPermission)
  if (!hasPermission) {
    logShakeDebug('motion listener not attached; permission unavailable')
    return
  }

  try {
    logShakeDebug('attaching motion listener')
    motionListener = await Motion.addListener('accel', (event) => {
      const acceleration =
        event.acceleration || event.accelerationIncludingGravity
      if (!acceleration) return

      const x = acceleration.x || 0
      const y = acceleration.y || 0
      const z = acceleration.z || 0
      const magnitude = Math.sqrt(x * x + y * y + z * z)
      const now = Date.now()

      if (now - lastMotionLogAt > 1000) {
        lastMotionLogAt = now
        logShakeDebug('motion sample', {
          x: Number(x.toFixed(2)),
          y: Number(y.toFixed(2)),
          z: Number(z.toFixed(2)),
          magnitude: Number(magnitude.toFixed(2)),
          interval: event.interval,
        })
      }

      if (magnitude < SHAKE_THRESHOLD) return

      recentShakeHits = recentShakeHits.filter(
        (timestamp) => now - timestamp < SHAKE_WINDOW_MS,
      )
      recentShakeHits.push(now)
      logShakeDebug('shake threshold hit', {
        magnitude: Number(magnitude.toFixed(2)),
        hits: recentShakeHits.length,
      })

      if (
        recentShakeHits.length >= 2 &&
        now - lastShakeAt > SHAKE_COOLDOWN_MS
      ) {
        openBugReportFromShake('motion')
      }
    })
  } catch (error) {
    motionListener = null
    console.warn('Shake-to-report motion listener failed to start:', error)
  }
}

export function useShakeToReportBug() {
  const { track } = usePostHog()

  const initializeShakeToReportBug = async () => {
    if (initialized.value) return
    initialized.value = true

    const preference = await Preferences.get({ key: SHAKE_ENABLED_KEY })
    enabled.value =
      preference.value === null ? true : preference.value === 'true'

    if (enabled.value) {
      await startListening({ requestPermission: false })
    }
  }

  const setShakeToReportBugEnabled = async (value: boolean) => {
    enabled.value = value
    await Preferences.set({ key: SHAKE_ENABLED_KEY, value: String(value) })
    track(AnalyticsEvent.SHAKE_TO_REPORT_TOGGLED, { enabled: value })

    if (value) {
      await startListening({ requestPermission: true })
    } else {
      await stopListening()
    }
  }

  const openBugReport = (source: 'shake' | 'manual' = 'manual') => {
    emitOpenBugReport(source)
  }

  return {
    enabled,
    initializeShakeToReportBug,
    openBugReport,
    setShakeToReportBugEnabled,
  }
}
